import { headingFonts, pxToRem, SEMANTIC_CSS_VAR_PREFIX, createThemedCssVarDefinitions } from "@creditornot/cb-utils";
import styled from "styled-components";
import { jsx } from "react/jsx-runtime";
import * as React from "react";
const baseHeadingStyle = `
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  font-variant-ligatures: common-ligatures;

  [data-cb-heading-font='${headingFonts.Omnes}'] && {
    font-feature-settings: 'kern', 'ss01', 'ss05', 'ss07';
    font-family: ${headingFonts.Omnes}, -apple-system, BlinkMacSystemFont, Roboto, 'Segoe UI', Arimo, 'Open Sans', sans-serif;
  }

  [data-cb-heading-font='${headingFonts.October}'] && {
    font-feature-settings: 'kern';
    font-family: ${headingFonts.October}, -apple-system, BlinkMacSystemFont, Roboto, 'Segoe UI', Arimo, 'Open Sans', sans-serif;
  }

  [data-cb-heading-font='${headingFonts.MPlus}'] && {
    font-feature-settings: 'kern', 'ss01', 'ss05', 'ss07';
    font-family: ${headingFonts.MPlus}, -apple-system, BlinkMacSystemFont, Roboto, 'Segoe UI', Arimo, 'Open Sans', sans-serif;
  }
`;
const baseBodyStyle = `
  font-feature-settings: 'kern';
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  font-family: -apple-system, BlinkMacSystemFont, Roboto, 'Segoe UI', Arimo, 'Open Sans', sans-serif;
`;
const fontWeights = {
  inherit: "inherit",
  normal: "400",
  semiBold: "500",
  bold: "700"
};
function decoratedFontStyle(cssStyle) {
  return ({ disableNestedWeightTags, weight = "normal" } = {}) => {
    return `
      ${cssStyle}
      font-weight: ${fontWeights[weight]};

      ${disableNestedWeightTags ? "" : `
        b, strong {
          font-weight: ${weight === "bold" || weight === "semiBold" ? "700" : "600"};
        }
      `}
    `;
  };
}
const typographyCss = {
  baseBodyStyle: () => baseBodyStyle,
  baseHeadingStyle: () => baseHeadingStyle,
  /** @deprecated use baseBodyStyle instead */
  baseFontFamily: () => baseBodyStyle,
  Heading1: () => `
    ${baseHeadingStyle}
    font-size: ${pxToRem(48)};
    line-height: ${pxToRem(52)};
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    text-transform: none;
  `,
  Heading2: () => `
    ${baseHeadingStyle}
    font-size: ${pxToRem(40)};
    line-height: ${pxToRem(44)};
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    text-transform: none;
  `,
  Heading3: () => `
    ${baseHeadingStyle}
    font-size: ${pxToRem(32)};
    line-height: ${pxToRem(36)};
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    text-transform: none;
  `,
  Heading4: () => `
    ${baseHeadingStyle}
    font-size: ${pxToRem(28)};
    line-height: ${pxToRem(32)};
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    text-transform: none;
  `,
  Heading5: () => `
    ${baseHeadingStyle}
    font-size: ${pxToRem(24)};
    line-height: ${pxToRem(28)};
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    text-transform: none;
  `,
  Heading6: () => `
    ${baseHeadingStyle}
    font-size: ${pxToRem(20)};
    line-height: ${pxToRem(24)};
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    text-transform: none;
  `,
  Title1: () => `
    ${baseBodyStyle}
    font-size: ${pxToRem(16)};
    line-height: ${pxToRem(20)};
    font-weight: bold;
    font-style: normal;
    font-stretch: normal;
  `,
  Title2: () => `
    ${baseBodyStyle}
    font-size: ${pxToRem(14)};
    line-height: ${pxToRem(20)};
    font-weight: bold;
    font-style: normal;
    font-stretch: normal;
  `,
  Subtitle: () => `
    ${baseBodyStyle}
    font-size: ${pxToRem(12)};
    line-height: ${pxToRem(16)};
    font-weight: bold;
    font-style: normal;
    font-stretch: normal;
  `,
  Body1: decoratedFontStyle(`
    ${baseBodyStyle}
    font-size: ${pxToRem(18)};
    line-height: ${pxToRem(24)};
    font-style: normal;
    font-stretch: normal;
  `),
  Body2: decoratedFontStyle(`
    ${baseBodyStyle}
    font-size: ${pxToRem(16)};
    line-height: ${pxToRem(20)};
    font-style: normal;
    font-stretch: normal;
  `),
  Body3: decoratedFontStyle(`
    ${baseBodyStyle}
    font-size: ${pxToRem(14)};
    line-height: ${pxToRem(20)};
    font-style: normal;
    font-stretch: normal;
  `),
  Small: decoratedFontStyle(`
    ${baseBodyStyle}
    font-size: ${pxToRem(12)};
    line-height: ${pxToRem(16)};
    font-weight: normal;
    font-style: normal;
    font-stretch: normal;
  `),
  Tiny: decoratedFontStyle(`
    ${baseBodyStyle}
    font-size: ${pxToRem(10)};
    line-height: ${pxToRem(12)};
    font-weight: normal;
    font-style: normal;
    font-stretch: normal;
  `),
  Overline1: () => `
    ${baseBodyStyle}
    text-transform: uppercase;
    font-size: ${pxToRem(16)};
    line-height: ${pxToRem(20)};
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
  `,
  Overline2: () => `
    ${baseBodyStyle}
    text-transform: uppercase;
    font-size: ${pxToRem(14)};
    line-height: ${pxToRem(20)};
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
  `,
  Overline3: () => `
    ${baseBodyStyle}
    text-transform: uppercase;
    font-size: ${pxToRem(12)};
    line-height: ${pxToRem(16)};
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
  `,
  Overline4: () => `
    ${baseBodyStyle}
    text-transform: uppercase;
    font-size: ${pxToRem(10)};
    line-height: ${pxToRem(12)};
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
  `,
  Quote1: () => `
    ${baseBodyStyle}
    font-size: ${pxToRem(16)};
    line-height: ${pxToRem(20)};
    font-weight: 500;
    font-style: italic;
    font-stretch: normal;
  `,
  Quote2: () => `
    ${baseBodyStyle}
    font-size: ${pxToRem(14)};
    line-height: ${pxToRem(20)};
    font-weight: 500;
    font-style: italic;
    font-stretch: normal;
  `
};
const Title1 = styled.span`
  ${typographyCss.Title1()}
`;
const Title2 = styled.span`
  ${typographyCss.Title2()}
`;
const Subtitle = styled.span`
  ${typographyCss.Subtitle()}
`;
const Body1 = styled.span`
  ${typographyCss.Body1()}
  font-weight: ${({ $fontWeight = "inherit" }) => $fontWeight === "inherit" ? "inherit" : fontWeights[$fontWeight]};
`;
const Body2 = styled.span`
  ${typographyCss.Body2()}
  font-weight: ${({ $fontWeight = "inherit" }) => $fontWeight === "inherit" ? "inherit" : fontWeights[$fontWeight]};
`;
const Body3 = styled.span`
  ${typographyCss.Body3()}
  font-weight: ${({ $fontWeight = "inherit" }) => $fontWeight === "inherit" ? "inherit" : fontWeights[$fontWeight]};
`;
const Small = styled.span`
  ${typographyCss.Small()}
`;
const Tiny = styled.span`
  ${typographyCss.Tiny()}
`;
const Overline1 = styled.span`
  ${typographyCss.Overline1()}
`;
const Overline2 = styled.span`
  ${typographyCss.Overline2()}
`;
const Overline3 = styled.span`
  ${typographyCss.Overline3()}
`;
const Overline4 = styled.span`
  ${typographyCss.Overline4()}
`;
const Quote1 = styled.span`
  ${typographyCss.Quote1()}
`;
const Quote2 = styled.span`
  ${typographyCss.Quote2()}
`;
const TypographyComponents = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  Body1,
  Body2,
  Body3,
  Overline1,
  Overline2,
  Overline3,
  Overline4,
  Quote1,
  Quote2,
  Small,
  Subtitle,
  Tiny,
  Title1,
  Title2
}, Symbol.toStringTag, { value: "Module" }));
const colors = {
  wolt100: "rgba(0, 157, 224, 1)",
  wolt96: "rgba(0, 157, 224, 0.96)",
  wolt92: "rgba(0, 157, 224, 0.92)",
  wolt88: "rgba(0, 157, 224, 0.88)",
  wolt84: "rgba(0, 157, 224, 0.84)",
  wolt80: "rgba(0, 157, 224, 0.80)",
  wolt76: "rgba(0, 157, 224, 0.76)",
  wolt72: "rgba(0, 157, 224, 0.72)",
  wolt68: "rgba(0, 157, 224, 0.68)",
  wolt64: "rgba(0, 157, 224, 0.64)",
  wolt60: "rgba(0, 157, 224, 0.60)",
  wolt56: "rgba(0, 157, 224, 0.56)",
  wolt52: "rgba(0, 157, 224, 0.52)",
  wolt48: "rgba(0, 157, 224, 0.48)",
  wolt44: "rgba(0, 157, 224, 0.44)",
  wolt40: "rgba(0, 157, 224, 0.40)",
  wolt36: "rgba(0, 157, 224, 0.36)",
  wolt32: "rgba(0, 157, 224, 0.32)",
  wolt28: "rgba(0, 157, 224, 0.28)",
  wolt24: "rgba(0, 157, 224, 0.24)",
  wolt20: "rgba(0, 157, 224, 0.20)",
  wolt16: "rgba(0, 157, 224, 0.16)",
  wolt12: "rgba(0, 157, 224, 0.12)",
  wolt8: "rgba(0, 157, 224, 0.08)",
  wolt4: "rgba(0, 157, 224, 0.04)",
  pepper100: "rgba(32, 33, 37, 1)",
  pepper96: "rgba(32, 33, 37, 0.96)",
  pepper92: "rgba(32, 33, 37, 0.92)",
  pepper88: "rgba(32, 33, 37, 0.88)",
  pepper84: "rgba(32, 33, 37, 0.84)",
  pepper80: "rgba(32, 33, 37, 0.80)",
  pepper76: "rgba(32, 33, 37, 0.76)",
  pepper72: "rgba(32, 33, 37, 0.72)",
  pepper68: "rgba(32, 33, 37, 0.68)",
  pepper64: "rgba(32, 33, 37, 0.64)",
  pepper60: "rgba(32, 33, 37, 0.60)",
  pepper56: "rgba(32, 33, 37, 0.56)",
  pepper52: "rgba(32, 33, 37, 0.52)",
  pepper48: "rgba(32, 33, 37, 0.48)",
  pepper44: "rgba(32, 33, 37, 0.44)",
  pepper40: "rgba(32, 33, 37, 0.40)",
  pepper36: "rgba(32, 33, 37, 0.36)",
  pepper32: "rgba(32, 33, 37, 0.32)",
  pepper28: "rgba(32, 33, 37, 0.28)",
  pepper24: "rgba(32, 33, 37, 0.24)",
  pepper20: "rgba(32, 33, 37, 0.20)",
  pepper16: "rgba(32, 33, 37, 0.16)",
  pepper12: "rgba(32, 33, 37, 0.12)",
  pepper8: "rgba(32, 33, 37, 0.08)",
  pepper4: "rgba(32, 33, 37, 0.04)",
  pepper2: "rgba(32, 33, 37, 0.02)",
  salt100: "rgba(255, 255, 255, 1)",
  salt96: "rgba(255, 255, 255, 0.96)",
  salt92: "rgba(255, 255, 255, 0.92)",
  salt88: "rgba(255, 255, 255, 0.88)",
  salt84: "rgba(255, 255, 255, 0.84)",
  salt80: "rgba(255, 255, 255, 0.80)",
  salt76: "rgba(255, 255, 255, 0.76)",
  salt72: "rgba(255, 255, 255, 0.72)",
  salt68: "rgba(255, 255, 255, 0.68)",
  salt64: "rgba(255, 255, 255, 0.64)",
  salt60: "rgba(255, 255, 255, 0.60)",
  salt56: "rgba(255, 255, 255, 0.56)",
  salt52: "rgba(255, 255, 255, 0.52)",
  salt48: "rgba(255, 255, 255, 0.48)",
  salt44: "rgba(255, 255, 255, 0.44)",
  salt40: "rgba(255, 255, 255, 0.40)",
  salt36: "rgba(255, 255, 255, 0.36)",
  salt32: "rgba(255, 255, 255, 0.32)",
  salt28: "rgba(255, 255, 255, 0.28)",
  salt24: "rgba(255, 255, 255, 0.24)",
  salt20: "rgba(255, 255, 255, 0.20)",
  salt16: "rgba(255, 255, 255, 0.16)",
  salt12: "rgba(255, 255, 255, 0.12)",
  salt8: "rgba(255, 255, 255, 0.08)",
  salt4: "rgba(255, 255, 255, 0.04)",
  darkSky100: "rgba(0, 120, 212, 1)",
  darkSky96: "rgba(0, 120, 212, 0.96)",
  darkSky92: "rgba(0, 120, 212, 0.92)",
  darkSky88: "rgba(0, 120, 212, 0.88)",
  darkSky84: "rgba(0, 120, 212, 0.84)",
  darkSky80: "rgba(0, 120, 212, 0.80)",
  darkSky76: "rgba(0, 120, 212, 0.76)",
  darkSky72: "rgba(0, 120, 212, 0.72)",
  darkSky68: "rgba(0, 120, 212, 0.68)",
  darkSky64: "rgba(0, 120, 212, 0.64)",
  darkSky60: "rgba(0, 120, 212, 0.60)",
  darkSky56: "rgba(0, 120, 212, 0.56)",
  darkSky52: "rgba(0, 120, 212, 0.52)",
  darkSky48: "rgba(0, 120, 212, 0.48)",
  darkSky44: "rgba(0, 120, 212, 0.44)",
  darkSky40: "rgba(0, 120, 212, 0.40)",
  darkSky36: "rgba(0, 120, 212, 0.36)",
  darkSky32: "rgba(0, 120, 212, 0.32)",
  darkSky28: "rgba(0, 120, 212, 0.28)",
  darkSky24: "rgba(0, 120, 212, 0.24)",
  darkSky20: "rgba(0, 120, 212, 0.20)",
  darkSky16: "rgba(0, 120, 212, 0.16)",
  darkSky12: "rgba(0, 120, 212, 0.12)",
  darkSky8: "rgba(0, 120, 212, 0.08)",
  darkSky4: "rgba(0, 120, 212, 0.04)",
  nightSky100: "rgba(15, 37, 148, 1)",
  nightSky96: "rgba(15, 37, 148, 0.96)",
  nightSky92: "rgba(15, 37, 148, 0.92)",
  nightSky88: "rgba(15, 37, 148, 0.88)",
  nightSky84: "rgba(15, 37, 148, 0.84)",
  nightSky80: "rgba(15, 37, 148, 0.80)",
  nightSky76: "rgba(15, 37, 148, 0.76)",
  nightSky72: "rgba(15, 37, 148, 0.72)",
  nightSky68: "rgba(15, 37, 148, 0.68)",
  nightSky64: "rgba(15, 37, 148, 0.64)",
  nightSky60: "rgba(15, 37, 148, 0.60)",
  nightSky56: "rgba(15, 37, 148, 0.56)",
  nightSky52: "rgba(15, 37, 148, 0.52)",
  nightSky48: "rgba(15, 37, 148, 0.48)",
  nightSky44: "rgba(15, 37, 148, 0.44)",
  nightSky40: "rgba(15, 37, 148, 0.40)",
  nightSky36: "rgba(15, 37, 148, 0.36)",
  nightSky32: "rgba(15, 37, 148, 0.32)",
  nightSky28: "rgba(15, 37, 148, 0.28)",
  nightSky24: "rgba(15, 37, 148, 0.24)",
  nightSky20: "rgba(15, 37, 148, 0.20)",
  nightSky16: "rgba(15, 37, 148, 0.16)",
  nightSky12: "rgba(15, 37, 148, 0.12)",
  nightSky8: "rgba(15, 37, 148, 0.08)",
  nightSky4: "rgba(15, 37, 148, 0.04)",
  space100: "rgba(0,0,0, 1)",
  space96: "rgba(0,0,0, 0.96)",
  space92: "rgba(0,0,0, 0.92)",
  space88: "rgba(0,0,0, 0.88)",
  space84: "rgba(0,0,0, 0.84)",
  space80: "rgba(0,0,0, 0.80)",
  space76: "rgba(0,0,0, 0.76)",
  space72: "rgba(0,0,0, 0.72)",
  space68: "rgba(0,0,0, 0.68)",
  space64: "rgba(0,0,0, 0.64)",
  space60: "rgba(0,0,0, 0.60)",
  space56: "rgba(0,0,0, 0.56)",
  space52: "rgba(0,0,0, 0.52)",
  space48: "rgba(0,0,0, 0.48)",
  space44: "rgba(0,0,0, 0.44)",
  space40: "rgba(0,0,0, 0.40)",
  space36: "rgba(0,0,0, 0.36)",
  space32: "rgba(0,0,0, 0.32)",
  space28: "rgba(0,0,0, 0.28)",
  space24: "rgba(0,0,0, 0.24)",
  space20: "rgba(0,0,0, 0.20)",
  space16: "rgba(0,0,0, 0.16)",
  space12: "rgba(0,0,0, 0.12)",
  space8: "rgba(0,0,0, 0.08)",
  space4: "rgba(0,0,0, 0.04)",
  lime100: "rgba(31, 199, 10, 1)",
  lime96: "rgba(31, 199, 10, 0.96)",
  lime92: "rgba(31, 199, 10, 0.92)",
  lime88: "rgba(31, 199, 10, 0.88)",
  lime84: "rgba(31, 199, 10, 0.84)",
  lime80: "rgba(31, 199, 10, 0.80)",
  lime76: "rgba(31, 199, 10, 0.76)",
  lime72: "rgba(31, 199, 10, 0.72)",
  lime68: "rgba(31, 199, 10, 0.68)",
  lime64: "rgba(31, 199, 10, 0.64)",
  lime60: "rgba(31, 199, 10, 0.60)",
  lime56: "rgba(31, 199, 10, 0.56)",
  lime52: "rgba(31, 199, 10, 0.52)",
  lime48: "rgba(31, 199, 10, 0.48)",
  lime44: "rgba(31, 199, 10, 0.44)",
  lime40: "rgba(31, 199, 10, 0.40)",
  lime36: "rgba(31, 199, 10, 0.36)",
  lime32: "rgba(31, 199, 10, 0.32)",
  lime28: "rgba(31, 199, 10, 0.28)",
  lime24: "rgba(31, 199, 10, 0.24)",
  lime20: "rgba(31, 199, 10, 0.20)",
  lime16: "rgba(31, 199, 10, 0.16)",
  lime12: "rgba(31, 199, 10, 0.12)",
  lime8: "rgba(31, 199, 10, 0.08)",
  lime4: "rgba(31, 199, 10, 0.04)",
  banana100: "rgba(254, 169, 13, 1)",
  banana96: "rgba(254, 169, 13, 0.96)",
  banana92: "rgba(254, 169, 13, 0.92)",
  banana88: "rgba(254, 169, 13, 0.88)",
  banana84: "rgba(254, 169, 13, 0.84)",
  banana80: "rgba(254, 169, 13, 0.80)",
  banana76: "rgba(254, 169, 13, 0.76)",
  banana72: "rgba(254, 169, 13, 0.72)",
  banana68: "rgba(254, 169, 13, 0.68)",
  banana64: "rgba(254, 169, 13, 0.64)",
  banana60: "rgba(254, 169, 13, 0.60)",
  banana56: "rgba(254, 169, 13, 0.56)",
  banana52: "rgba(254, 169, 13, 0.52)",
  banana48: "rgba(254, 169, 13, 0.48)",
  banana44: "rgba(254, 169, 13, 0.44)",
  banana40: "rgba(254, 169, 13, 0.40)",
  banana36: "rgba(254, 169, 13, 0.36)",
  banana32: "rgba(254, 169, 13, 0.32)",
  banana28: "rgba(254, 169, 13, 0.28)",
  banana24: "rgba(254, 169, 13, 0.24)",
  banana20: "rgba(254, 169, 13, 0.20)",
  banana16: "rgba(254, 169, 13, 0.16)",
  banana12: "rgba(254, 169, 13, 0.12)",
  banana8: "rgba(254, 169, 13, 0.08)",
  banana4: "rgba(254, 169, 13, 0.04)",
  orange100: "rgba(252, 98, 0, 1)",
  orange96: "rgba(252, 98, 0, 0.96)",
  orange92: "rgba(252, 98, 0, 0.92)",
  orange88: "rgba(252, 98, 0, 0.88)",
  orange84: "rgba(252, 98, 0, 0.84)",
  orange80: "rgba(252, 98, 0, 0.80)",
  orange76: "rgba(252, 98, 0, 0.76)",
  orange72: "rgba(252, 98, 0, 0.72)",
  orange68: "rgba(252, 98, 0, 0.68)",
  orange64: "rgba(252, 98, 0, 0.64)",
  orange60: "rgba(252, 98, 0, 0.60)",
  orange56: "rgba(252, 98, 0, 0.56)",
  orange52: "rgba(252, 98, 0, 0.52)",
  orange48: "rgba(252, 98, 0, 0.48)",
  orange44: "rgba(252, 98, 0, 0.44)",
  orange40: "rgba(252, 98, 0, 0.40)",
  orange36: "rgba(252, 98, 0, 0.36)",
  orange32: "rgba(252, 98, 0, 0.32)",
  orange28: "rgba(252, 98, 0, 0.28)",
  orange24: "rgba(252, 98, 0, 0.24)",
  orange20: "rgba(252, 98, 0, 0.20)",
  orange16: "rgba(252, 98, 0, 0.16)",
  orange12: "rgba(252, 98, 0, 0.12)",
  orange8: "rgba(252, 98, 0, 0.08)",
  orange4: "rgba(252, 98, 0, 0.04)",
  strawberry100: "rgba(249, 58, 37, 1)",
  strawberry96: "rgba(249, 58, 37, 0.96)",
  strawberry92: "rgba(249, 58, 37, 0.92)",
  strawberry88: "rgba(249, 58, 37, 0.88)",
  strawberry84: "rgba(249, 58, 37, 0.84)",
  strawberry80: "rgba(249, 58, 37, 0.80)",
  strawberry76: "rgba(249, 58, 37, 0.76)",
  strawberry72: "rgba(249, 58, 37, 0.72)",
  strawberry68: "rgba(249, 58, 37, 0.68)",
  strawberry64: "rgba(249, 58, 37, 0.64)",
  strawberry60: "rgba(249, 58, 37, 0.60)",
  strawberry56: "rgba(249, 58, 37, 0.56)",
  strawberry52: "rgba(249, 58, 37, 0.52)",
  strawberry48: "rgba(249, 58, 37, 0.48)",
  strawberry44: "rgba(249, 58, 37, 0.44)",
  strawberry40: "rgba(249, 58, 37, 0.40)",
  strawberry36: "rgba(249, 58, 37, 0.36)",
  strawberry32: "rgba(249, 58, 37, 0.32)",
  strawberry28: "rgba(249, 58, 37, 0.28)",
  strawberry24: "rgba(249, 58, 37, 0.24)",
  strawberry20: "rgba(249, 58, 37, 0.20)",
  strawberry16: "rgba(249, 58, 37, 0.16)",
  strawberry12: "rgba(249, 58, 37, 0.12)",
  strawberry8: "rgba(249, 58, 37, 0.08)",
  strawberry4: "rgba(249, 58, 37, 0.04)",
  plum100: "rgba(135, 29, 196, 1)",
  plum96: "rgba(135, 29, 196, 0.96)",
  plum92: "rgba(135, 29, 196, 0.92)",
  plum88: "rgba(135, 29, 196, 0.88)",
  plum84: "rgba(135, 29, 196, 0.84)",
  plum80: "rgba(135, 29, 196, 0.80)",
  plum76: "rgba(135, 29, 196, 0.76)",
  plum72: "rgba(135, 29, 196, 0.72)",
  plum68: "rgba(135, 29, 196, 0.68)",
  plum64: "rgba(135, 29, 196, 0.64)",
  plum60: "rgba(135, 29, 196, 0.60)",
  plum56: "rgba(135, 29, 196, 0.56)",
  plum52: "rgba(135, 29, 196, 0.52)",
  plum48: "rgba(135, 29, 196, 0.48)",
  plum44: "rgba(135, 29, 196, 0.44)",
  plum40: "rgba(135, 29, 196, 0.40)",
  plum36: "rgba(135, 29, 196, 0.36)",
  plum32: "rgba(135, 29, 196, 0.32)",
  plum28: "rgba(135, 29, 196, 0.28)",
  plum24: "rgba(135, 29, 196, 0.24)",
  plum20: "rgba(135, 29, 196, 0.20)",
  plum16: "rgba(135, 29, 196, 0.16)",
  plum12: "rgba(135, 29, 196, 0.12)",
  plum8: "rgba(135, 29, 196, 0.08)",
  plum4: "rgba(135, 29, 196, 0.04)"
};
Object.freeze(colors);
const semanticColors = {
  light: {
    textPrimary: colors.pepper100,
    textSecondary: colors.pepper64,
    textTertiary: colors.pepper40,
    surfaceMain: colors.salt100,
    /** @deprecated use `borderLight` instead */
    borderPrimary: colors.pepper12,
    borderLight: colors.pepper12,
    a11yFocusIndicator: colors.nightSky100
  },
  lightHighContrast: {
    textPrimary: colors.pepper100,
    textSecondary: colors.pepper80,
    textTertiary: colors.pepper80,
    surfaceMain: colors.salt100,
    /** @deprecated use `borderLight` instead */
    borderPrimary: colors.pepper80,
    borderLight: colors.pepper80,
    a11yFocusIndicator: colors.nightSky100
  },
  dark: {
    textPrimary: colors.salt100,
    textSecondary: colors.salt72,
    textTertiary: colors.salt48,
    surfaceMain: "#141414",
    /** @deprecated use `borderLight` instead */
    borderPrimary: colors.salt16,
    borderLight: colors.salt16,
    a11yFocusIndicator: colors.banana100
  }
};
Object.freeze(semanticColors);
const fallbacks = {
  borderLight: "borderPrimary",
  borderPrimary: "borderLight"
};
const createVarName = (key) => `--${SEMANTIC_CSS_VAR_PREFIX}-${key}`;
const cbSemanticCssVars = Object.fromEntries(
  Object.keys(semanticColors.light).map((key) => {
    const fallbackKey = fallbacks[key];
    return [
      key,
      `var(${createVarName(key)}${fallbackKey ? `, var(${createVarName(fallbackKey)})` : ""})`
    ];
  })
);
const experimental_cbSemanticCssVars = {
  /**
   * A user interface background.
   */
  bg: "var(--cb-color-bg)",
  /**
   * A surface that's interactive.
   */
  bgSurface: "var(--cb-color-bg-surface)",
  /**
   * A surface that's interactive and hovered.
   */
  bgSurfaceHovered: "var(--cb-color-bg-surface-hovered)",
  /**
   * A surface that's interactive and pressed.
   */
  bgSurfacePressed: "var(--cb-color-bg-surface-pressed)",
  /**
   * A surface that's interactive and selected.
   */
  bgSurfaceSelected: "var(--cb-color-bg-surface-selected)",
  /**
   * A surface that's interactive and unavailable.
   */
  bgSurfaceDisabled: "var(--cb-color-bg-surface-disabled)",
  /**
   * A surface that's interactive.
   */
  bgSurfaceSecondary: "var(--cb-color-bg-surface-secondary)",
  /**
   * A surface that's interactive and hovered.
   */
  bgSurfaceSecondaryHovered: "var(--cb-color-bg-surface-secondary-hovered)",
  /**
   * A surface that's interactive and pressed.
   */
  bgSurfaceSecondaryPressed: "var(--cb-color-bg-surface-secondary-pressed)",
  /**
   * A surface that's interactive and selected.
   */
  bgSurfaceSecondarySelected: "var(--cb-color-bg-surface-secondary-selected)",
  /**
   * A surface that's interactive and unavailable.
   */
  bgSurfaceSecondaryDisabled: "var(--cb-color-bg-surface-secondary-disabled)",
  /**
   * A surface that's interactive.
   */
  bgSurfaceTertiary: "var(--cb-color-bg-surface-tertiary)",
  /**
   * A surface that's interactive and hovered.
   */
  bgSurfaceTertiaryHovered: "var(--cb-color-bg-surface-tertiary-hovered)",
  /**
   * A surface that's interactive and pressed.
   */
  bgSurfaceTertiaryPressed: "var(--cb-color-bg-surface-tertiary-pressed)",
  /**
   * A surface that's interactive and selected.
   */
  bgSurfaceTertiarySelected: "var(--cb-color-bg-surface-tertiary-selected)",
  /**
   * A surface that's interactive and unavailable.
   */
  bgSurfaceTertiaryDisabled: "var(--cb-color-bg-surface-tertiary-disabled)",
  /**
   * A surface that's interactive.
   */
  bgFill: "var(--cb-color-bg-fill)",
  /**
   * A surface that's interactive and hovered.
   */
  bgFillHovered: "var(--cb-color-bg-fill-hovered)",
  /**
   * A surface that's interactive and pressed.
   */
  bgFillPressed: "var(--cb-color-bg-fill-pressed)",
  /**
   * A surface that's interactive and selected.
   */
  bgFillSelected: "var(--cb-color-bg-fill-selected)",
  /**
   * A surface that's interactive and unavailable.
   */
  bgFillDisabled: "var(--cb-color-bg-fill-disabled)",
  /**
   * A label containing content of importance.
   */
  text: "var(--cb-color-text)",
  /**
   * A label containing content of lesser importance.
   */
  textSubdued: "var(--cb-color-text-subdued)",
  /**
   * A label that’s interactive and unavailable.
   */
  textDisabled: "var(--cb-color-text-disabled)",
  /**
   * A label on a surface that's interactive.
   */
  textOnBgFill: "var(--cb-color-text-on-bg-fill)",
  /**
   * A label on a surface that's interactive and hovered.
   */
  textOnBgFillHovered: "var(--cb-color-text-on-bg-fill-hovered)",
  /**
   * A label on a surface that's interactive and pressed.
   */
  textOnBgFillPressed: "var(--cb-color-text-on-bg-fill-pressed)",
  /**
   * A label on a surface that's interactive and selected.
   */
  textOnBgFillSelected: "var(--cb-color-text-on-bg-fill-selected)",
  /**
   * A label on a surface that's interactive and unavailable.
   */
  textOnBgFillDisabled: "var(--cb-color-text-on-bg-fill-disabled)",
  /**
   * A boundery of an item that's interactive.
   */
  border: "var(--cb-color-border)",
  /**
   * A boundery of an item that's interactive and hovered.
   */
  borderHovered: "var(--cb-color-border-hovered)",
  /**
   * A boundery of an item that's interactive and pressed.
   */
  borderPressed: "var(--cb-color-border-pressed)",
  /**
   * A boundery of an item that's interactive and selected.
   */
  borderSelected: "var(--cb-color-border-selected)",
  /**
   * A boundery of an item that's interactive and unavailable.
   */
  borderDisabled: "var(--cb-color-border-disabled)",
  /**
   * A boundery of an item that's decorative.
   */
  borderSubdued: "var(--cb-color-border-subdued)",
  /**
   * A boundery of an item alerting of an negative.
   */
  borderNegative: "var(--cb-color-border-negative)",
  /**
   * A boundery of an item that's on focus.
   */
  outlineFocused: "var(--cb-color-outline-focused)",
  /**
   * A surface that's interactive.
   */
  bgSurfaceBrand: "var(--cb-color-bg-surface-brand)",
  /**
   * A surface that's interactive and hovered.
   */
  bgSurfaceBrandHovered: "var(--cb-color-bg-surface-brand-hovered)",
  /**
   * A surface that's interactive and pressed.
   */
  bgSurfaceBrandPressed: "var(--cb-color-bg-surface-brand-pressed)",
  /**
   * A surface that's interactive and selected.
   */
  bgSurfaceBrandSelected: "var(--cb-color-bg-surface-brand-selected)",
  /**
   * A surface that's interactive and unavailable.
   */
  bgSurfaceBrandDisabled: "var(--cb-color-bg-surface-brand-disabled)",
  /**
   * A surface that's interactive.
   */
  bgFillBrand: "var(--cb-color-bg-fill-brand)",
  /**
   * A surface that's interactive and hovered.
   */
  bgFillBrandHovered: "var(--cb-color-bg-fill-brand-hovered)",
  /**
   * A surface that's interactive and pressed.
   */
  bgFillBrandPressed: "var(--cb-color-bg-fill-brand-pressed)",
  /**
   * A surface that's interactive and selected.
   */
  bgFillBrandSelected: "var(--cb-color-bg-fill-brand-selected)",
  /**
   * A surface that's interactive and unavailable.
   */
  bgFillBrandDisabled: "var(--cb-color-bg-fill-brand-disabled)",
  /**
   * A label that's interactive.
   */
  textBrand: "var(--cb-color-text-brand)",
  /**
   * A label that's interactive and hovered.
   */
  textBrandHovered: "var(--cb-color-text-brand-hovered)",
  /**
   * A label that's interactive and pressed.
   */
  textBrandPressed: "var(--cb-color-text-brand-pressed)",
  /**
   * A label that's interactive and selected.
   */
  textBrandSelected: "var(--cb-color-text-brand-selected)",
  /**
   * A label that's interactive and unavailable.
   */
  textBrandDisabled: "var(--cb-color-text-brand-disabled)",
  /**
   * A label on a surface that's interactive.
   */
  textOnBgFillBrand: "var(--cb-color-text-on-bg-fill-brand)",
  /**
   * A label on a surface that's interactive and hovered.
   */
  textOnBgFillBrandHovered: "var(--cb-color-text-on-bg-fill-brand-hovered)",
  /**
   * A label on a surface that's interactive and pressed.
   */
  textOnBgFillBrandPressed: "var(--cb-color-text-on-bg-fill-brand-pressed)",
  /**
   * A label on a surface that's interactive and selected.
   */
  textOnBgFillBrandSelected: "var(--cb-color-text-on-bg-fill-brand-selected)",
  /**
   * A label on a surface that's interactive and unavailable.
   */
  textOnBgFillBrandDisabled: "var(--cb-color-text-on-bg-fill-brand-disabled)",
  /**
   * A surface that's interactive.
   */
  bgSurfaceNegative: "var(--cb-color-bg-surface-negative)",
  /**
   * A surface that's interactive and hovered.
   */
  bgSurfaceNegativeHovered: "var(--cb-color-bg-surface-negative-hovered)",
  /**
   * A surface that's interactive and pressed.
   */
  bgSurfaceNegativePressed: "var(--cb-color-bg-surface-negative-pressed)",
  /**
   * A surface that's interactive and selected.
   */
  bgSurfaceNegativeSelected: "var(--cb-color-bg-surface-negative-selected)",
  /**
   * A surface that's interactive and unavailable.
   */
  bgSurfaceNegativeDisabled: "var(--cb-color-bg-surface-negative-disabled)",
  /**
   * A surface that's interactive.
   */
  bgFillNegative: "var(--cb-color-bg-fill-negative)",
  /**
   * A surface that's interactive and hovered.
   */
  bgFillNegativeHovered: "var(--cb-color-bg-fill-negative-hovered)",
  /**
   * A surface that's interactive and pressed.
   */
  bgFillNegativePressed: "var(--cb-color-bg-fill-negative-pressed)",
  /**
   * A surface that's interactive and selected.
   */
  bgFillNegativeSelected: "var(--cb-color-bg-fill-negative-selected)",
  /**
   * A surface that's interactive and unavailable.
   */
  bgFillNegativeDisabled: "var(--cb-color-bg-fill-negative-disabled)",
  /**
   * A label that's interactive.
   */
  textNegative: "var(--cb-color-text-negative)",
  /**
   * A label that's interactive and hovered.
   */
  textNegativeHovered: "var(--cb-color-text-negative-hovered)",
  /**
   * A label that's interactive and pressed.
   */
  textNegativePressed: "var(--cb-color-text-negative-pressed)",
  /**
   * A label that's interactive and selected.
   */
  textNegativeSelected: "var(--cb-color-text-negative-selected)",
  /**
   * A label that's interactive and unavailable.
   */
  textNegativeDisabled: "var(--cb-color-text-negative-disabled)",
  /**
   * A label on a surface that's interactive.
   */
  textOnBgFillNegative: "var(--cb-color-text-on-bg-fill-negative)",
  /**
   * A label on a surface that's interactive and hovered.
   */
  textOnBgFillNegativeHovered: "var(--cb-color-text-on-bg-fill-negative-hovered)",
  /**
   * A label on a surface that's interactive and pressed.
   */
  textOnBgFillNegativePressed: "var(--cb-color-text-on-bg-fill-negative-pressed)",
  /**
   * A label on a surface that's interactive and selected.
   */
  textOnBgFillNegativeSelected: "var(--cb-color-text-on-bg-fill-negative-selected)",
  /**
   * A label on a surface that's interactive and unavailable.
   */
  textOnBgFillNegativeDisabled: "var(--cb-color-text-on-bg-fill-negative-disabled)",
  /**
   * A surface that's interactive.
   */
  bgSurfaceWarning: "var(--cb-color-bg-surface-warning)",
  /**
   * A surface that's interactive and hovered.
   */
  bgSurfaceWarningHovered: "var(--cb-color-bg-surface-warning-hovered)",
  /**
   * A surface that's interactive and pressed.
   */
  bgSurfaceWarningPressed: "var(--cb-color-bg-surface-warning-pressed)",
  /**
   * A surface that's interactive and selected.
   */
  bgSurfaceWarningSelected: "var(--cb-color-bg-surface-warning-selected)",
  /**
   * A surface that's interactive and unavailable.
   */
  bgSurfaceWarningDisabled: "var(--cb-color-bg-surface-warning-disabled)",
  /**
   * A surface that's interactive.
   */
  bgFillWarning: "var(--cb-color-bg-fill-warning)",
  /**
   * A surface that's interactive and hovered.
   */
  bgFillWarningHovered: "var(--cb-color-bg-fill-warning-hovered)",
  /**
   * A surface that's interactive and pressed.
   */
  bgFillWarningPressed: "var(--cb-color-bg-fill-warning-pressed)",
  /**
   * A surface that's interactive and selected.
   */
  bgFillWarningSelected: "var(--cb-color-bg-fill-warning-selected)",
  /**
   * A surface that's interactive and unavailable.
   */
  bgFillWarningDisabled: "var(--cb-color-bg-fill-warning-disabled)",
  /**
   * A label that's interactive.
   */
  textWarning: "var(--cb-color-text-warning)",
  /**
   * A label that's interactive and hovered.
   */
  textWarningHovered: "var(--cb-color-text-warning-hovered)",
  /**
   * A label that's interactive and pressed.
   */
  textWarningPressed: "var(--cb-color-text-warning-pressed)",
  /**
   * A label that's interactive and selected.
   */
  textWarningSelected: "var(--cb-color-text-warning-selected)",
  /**
   * A label that's interactive and unavailable.
   */
  textWarningDisabled: "var(--cb-color-text-warning-disabled)",
  /**
   * A label on a surface that's interactive.
   */
  textOnBgFillWarning: "var(--cb-color-text-on-bg-fill-warning)",
  /**
   * A label on a surface that's interactive and hovered.
   */
  textOnBgFillWarningHovered: "var(--cb-color-text-on-bg-fill-warning-hovered)",
  /**
   * A label on a surface that's interactive and pressed.
   */
  textOnBgFillWarningPressed: "var(--cb-color-text-on-bg-fill-warning-pressed)",
  /**
   * A label on a surface that's interactive and selected.
   */
  textOnBgFillWarningSelected: "var(--cb-color-text-on-bg-fill-warning-selected)",
  /**
   * A label on a surface that's interactive and unavailable.
   */
  textOnBgFillWarningDisabled: "var(--cb-color-text-on-bg-fill-warning-disabled)",
  /**
   * A surface that's interactive.
   */
  bgSurfacePositive: "var(--cb-color-bg-surface-positive)",
  /**
   * A surface that's interactive and hovered.
   */
  bgSurfacePositiveHovered: "var(--cb-color-bg-surface-positive-hovered)",
  /**
   * A surface that's interactive and pressed.
   */
  bgSurfacePositivePressed: "var(--cb-color-bg-surface-positive-pressed)",
  /**
   * A surface that's interactive and selected.
   */
  bgSurfacePositiveSelected: "var(--cb-color-bg-surface-positive-selected)",
  /**
   * A surface that's interactive and unavailable.
   */
  bgSurfacePositiveDisabled: "var(--cb-color-bg-surface-positive-disabled)",
  /**
   * A surface that's interactive.
   */
  bgFillPositive: "var(--cb-color-bg-fill-positive)",
  /**
   * A surface that's interactive and hovered.
   */
  bgFillPositiveHovered: "var(--cb-color-bg-fill-positive-hovered)",
  /**
   * A surface that's interactive and pressed.
   */
  bgFillPositivePressed: "var(--cb-color-bg-fill-positive-pressed)",
  /**
   * A surface that's interactive and selected.
   */
  bgFillPositiveSelected: "var(--cb-color-bg-fill-positive-selected)",
  /**
   * A surface that's interactive and unavailable.
   */
  bgFillPositiveDisabled: "var(--cb-color-bg-fill-positive-disabled)",
  /**
   * A label that's interactive.
   */
  textPositive: "var(--cb-color-text-positive)",
  /**
   * A label that's interactive and hovered.
   */
  textPositiveHovered: "var(--cb-color-text-positive-hovered)",
  /**
   * A label that's interactive and pressed.
   */
  textPositivePressed: "var(--cb-color-text-positive-pressed)",
  /**
   * A label that's interactive and selected.
   */
  textPositiveSelected: "var(--cb-color-text-positive-selected)",
  /**
   * A label that's interactive and unavailable.
   */
  textPositiveDisabled: "var(--cb-color-text-positive-disabled)",
  /**
   * A label on a surface that's interactive.
   */
  textOnBgFillPositive: "var(--cb-color-text-on-bg-fill-positive)",
  /**
   * A label on a surface that's interactive and hovered.
   */
  textOnBgFillPositiveHovered: "var(--cb-color-text-on-bg-fill-positive-hovered)",
  /**
   * A label on a surface that's interactive and pressed.
   */
  textOnBgFillPositivePressed: "var(--cb-color-text-on-bg-fill-positive-pressed)",
  /**
   * A label on a surface that's interactive and selected.
   */
  textOnBgFillPositiveSelected: "var(--cb-color-text-on-bg-fill-positive-selected)",
  /**
   * A label on a surface that's interactive and unavailable.
   */
  textOnBgFillPositiveDisabled: "var(--cb-color-text-on-bg-fill-positive-disabled)",
  /**
   * A surface that's interactive.
   */
  bgSurfaceInverse: "var(--cb-color-bg-surface-inverse)",
  /**
   * A surface that's interactive and hovered.
   */
  bgSurfaceInverseHovered: "var(--cb-color-bg-surface-inverse-hovered)",
  /**
   * A surface that's interactive and pressed.
   */
  bgSurfaceInversePressed: "var(--cb-color-bg-surface-inverse-pressed)",
  /**
   * A surface that's interactive and selected.
   */
  bgSurfaceInverseSelected: "var(--cb-color-bg-surface-inverse-selected)",
  /**
   * A surface that's interactive and unavailable.
   */
  bgSurfaceInverseDisabled: "var(--cb-color-bg-surface-inverse-disabled)",
  /**
   * A surface that's interactive.
   */
  bgFillInverse: "var(--cb-color-bg-fill-inverse)",
  /**
   * A surface that's interactive and hovered.
   */
  bgFillInverseHovered: "var(--cb-color-bg-fill-inverse-hovered)",
  /**
   * A surface that's interactive and pressed.
   */
  bgFillInversePressed: "var(--cb-color-bg-fill-inverse-pressed)",
  /**
   * A surface that's interactive and selected.
   */
  bgFillInverseSelected: "var(--cb-color-bg-fill-inverse-selected)",
  /**
   * A surface that's interactive and unavailable.
   */
  bgFillInverseDisabled: "var(--cb-color-bg-fill-inverse-disabled)",
  /**
   * A label that's interactive.
   */
  textInverse: "var(--cb-color-text-inverse)",
  /**
   * A label that's interactive and hovered.
   */
  textInverseHovered: "var(--cb-color-text-inverse-hovered)",
  /**
   * A label that's interactive and pressed.
   */
  textInversePressed: "var(--cb-color-text-inverse-pressed)",
  /**
   * A label that's interactive and selected.
   */
  textInverseSelected: "var(--cb-color-text-inverse-selected)",
  /**
   * A label that's interactive and unavailable.
   */
  textInverseDisabled: "var(--cb-color-text-inverse-disabled)",
  /**
   * A label on a surface that's interactive.
   */
  textOnBgFillInverse: "var(--cb-color-text-on-bg-fill-inverse)",
  /**
   * A label on a surface that's interactive and hovered.
   */
  textOnBgFillInverseHovered: "var(--cb-color-text-on-bg-fill-inverse-hovered)",
  /**
   * A label on a surface that's interactive and pressed.
   */
  textOnBgFillInversePressed: "var(--cb-color-text-on-bg-fill-inverse-pressed)",
  /**
   * A label on a surface that's interactive and selected.
   */
  textOnBgFillInverseSelected: "var(--cb-color-text-on-bg-fill-inverse-selected)",
  /**
   * A label on a surface that's interactive and unavailable.
   */
  textOnBgFillInverseDisabled: "var(--cb-color-text-on-bg-fill-inverse-disabled)",
  /**
   * A surface that's interactive.
   */
  bgSurfaceTransparent: "var(--cb-color-bg-surface-transparent)",
  /**
   * A surface that's interactive and hovered.
   */
  bgSurfaceTransparentHovered: "var(--cb-color-bg-surface-transparent-hovered)",
  /**
   * A surface that's interactive and pressed.
   */
  bgSurfaceTransparentPressed: "var(--cb-color-bg-surface-transparent-pressed)",
  /**
   * A surface that's interactive and selected.
   */
  bgSurfaceTransparentSelected: "var(--cb-color-bg-surface-transparent-selected)",
  /**
   * A surface that's interactive and unavailable.
   */
  bgSurfaceTransparentDisabled: "var(--cb-color-bg-surface-transparent-disabled)",
  /**
   * A surface that's interactive.
   */
  bgFillTransparent: "var(--cb-color-bg-fill-transparent)",
  /**
   * A surface that's interactive and hovered.
   */
  bgFillTransparentHovered: "var(--cb-color-bg-fill-transparent-hovered)",
  /**
   * A surface that's interactive and pressed.
   */
  bgFillTransparentPressed: "var(--cb-color-bg-fill-transparent-pressed)",
  /**
   * A surface that's interactive and selected.
   */
  bgFillTransparentSelected: "var(--cb-color-bg-fill-transparent-selected)",
  /**
   * A surface that's interactive and unavailable.
   */
  bgFillTransparentDisabled: "var(--cb-color-bg-fill-transparent-disabled)",
  /**
   * A label that's interactive.
   */
  textTransparent: "var(--cb-color-text-transparent)",
  /**
   * A label that's interactive and hovered.
   */
  textTransparentHovered: "var(--cb-color-text-transparent-hovered)",
  /**
   * A label that's interactive and pressed.
   */
  textTransparentPressed: "var(--cb-color-text-transparent-pressed)",
  /**
   * A label that's interactive and selected.
   */
  textTransparentSelected: "var(--cb-color-text-transparent-selected)",
  /**
   * A label that's interactive and unavailable.
   */
  textTransparentDisabled: "var(--cb-color-text-transparent-disabled)",
  /**
   * A label on a surface that's interactive.
   */
  textOnBgFillTransparent: "var(--cb-color-text-on-bg-fill-transparent)",
  /**
   * A label on a surface that's interactive and hovered.
   */
  textOnBgFillTransparentHovered: "var(--cb-color-text-on-bg-fill-transparent-hovered)",
  /**
   * A label on a surface that's interactive and pressed.
   */
  textOnBgFillTransparentPressed: "var(--cb-color-text-on-bg-fill-transparent-pressed)",
  /**
   * A label on a surface that's interactive and selected.
   */
  textOnBgFillTransparentSelected: "var(--cb-color-text-on-bg-fill-transparent-selected)",
  /**
   * A label on a surface that's interactive and unavailable.
   */
  textOnBgFillTransparentDisabled: "var(--cb-color-text-on-bg-fill-transparent-disabled)",
  /**
   * A surface that overlays an interface.
   */
  bgOverlay: "var(--cb-color-bg-overlay)",
  /**
   * A fixed color.
   */
  white: "var(--cb-color-white)",
  /**
   * A fixed color.
   */
  black: "var(--cb-color-black)"
};
Object.freeze(experimental_cbSemanticCssVars);
const darkModeElevationBGColors = {
  0: "#171717",
  1: "#181818",
  2: "#191919",
  3: "#191919",
  4: "#1C1C1C",
  6: "#1F1F1F",
  8: "#212121",
  12: "#242424",
  16: "#262626",
  24: "#303030"
};
Object.freeze(darkModeElevationBGColors);
const elevationCss = (level) => {
  const backgroundElevation = typeof level === "object" ? {
    light: level.light,
    dark: level.dark.backgroundElevation
  } : { light: level, dark: level };
  const shadowElevation = typeof level === "object" ? {
    light: level.light,
    dark: level.dark.shadowElevation
  } : { light: level, dark: level };
  const bgCss = `
    ${createThemedCssVarDefinitions({
    backgroundElevated: {
      light: colors.salt100,
      lightHighContrast: colors.salt100,
      dark: darkModeElevationBGColors[backgroundElevation.dark]
    }
  })}

    background-color: var(--backgroundElevated);
  `;
  if (shadowElevation.light === 0 && shadowElevation.dark === 0) {
    return bgCss;
  }
  return `
    ${bgCss}

    ${createThemedCssVarDefinitions({
    boxShadowElevated: {
      light: `0 0 ${pxToRem(shadowElevation.light)} 0 rgba(0, 0, 0, 0.06),
          0 ${pxToRem(shadowElevation.light)} ${pxToRem(
        shadowElevation.light
      )} 0 rgba(0, 0, 0, 0.12)`,
      lightHighContrast: `0 0 ${pxToRem(shadowElevation.light)} 0 rgba(0, 0, 0, 0.8),
          0 ${pxToRem(shadowElevation.light)} ${pxToRem(
        shadowElevation.light
      )} 0 rgba(0, 0, 0, 0.12)`,
      dark: `0 0 ${pxToRem(shadowElevation.dark)} 0 rgba(0, 0, 0, 0.06),
          0 ${pxToRem(shadowElevation.dark)} ${pxToRem(
        shadowElevation.dark
      )} 0 rgba(0, 0, 0, 0.12)`
    }
  })}

    box-shadow: var(--boxShadowElevated);
  `;
};
const replacePxValuesWithRem = (input) => input.replace(/(\d*\.?\d+)px/g, (_match, pxValue) => pxToRem(parseFloat(pxValue)));
const ShadowSizes = {
  xs: {
    dark: replacePxValuesWithRem(
      "0px 1px 2px 0px rgba(0, 0, 0, 0.36), 0px 0px 1px 0px rgba(0, 0, 0, 0.48)"
    ),
    light: replacePxValuesWithRem(
      "0px 1px 2px 0px rgba(0, 0, 0, 0.16), 0px 0px 1px 0px rgba(0, 0, 0, 0.20)"
    )
  },
  sm: {
    dark: replacePxValuesWithRem(
      "0px 2px 8px 0px rgba(0, 0, 0, 0.36), 0px 1px 2px 0px rgba(0, 0, 0, 0.48), 0px 0px 1px 0px rgba(0, 0, 0, 0.36)"
    ),
    light: replacePxValuesWithRem(
      "0px 2px 8px 0px rgba(0, 0, 0, 0.12), 0px 1px 2px 0px rgba(0, 0, 0, 0.12), 0px 0px 1px 0px rgba(0, 0, 0, 0.16)"
    )
  },
  md: {
    dark: replacePxValuesWithRem(
      "0px 4px 12px 0px rgba(0, 0, 0, 0.36), 0px 1px 2px 0px rgba(0, 0, 0, 0.48), 0px 0px 1px 0px rgba(0, 0, 0, 0.36)"
    ),
    light: replacePxValuesWithRem(
      "0px 4px 12px 0px rgba(0, 0, 0, 0.16), 0px 1px 2px 0px rgba(0, 0, 0, 0.12), 0px 0px 1px 0px rgba(0, 0, 0, 0.16)"
    )
  },
  lg: {
    dark: replacePxValuesWithRem(
      "0px 8px 28px 0px rgba(0, 0, 0, 0.48), 0px 2px 6px 0px rgba(0, 0, 0, 0.36), 0px 0px 1px 0px rgba(0, 0, 0, 0.36)"
    ),
    light: replacePxValuesWithRem(
      "0px 8px 28px 0px rgba(0, 0, 0, 0.16), 0px 2px 6px 0px rgba(0, 0, 0, 0.12), 0px 0px 1px 0px rgba(0, 0, 0, 0.16)"
    )
  }
};
const experimental_elevationCss = (level) => {
  return `
  background-color: ${experimental_cbSemanticCssVars.bg};
    ${createThemedCssVarDefinitions({
    boxShadowElevated: {
      dark: ShadowSizes[level].dark,
      light: ShadowSizes[level].light,
      lightHighContrast: ShadowSizes[level].light
    }
  })}

    box-shadow: var(--boxShadowElevated);
  `;
};
const experimental_cbElevatedClassName = "cb-elevated";
const WoltLogo = React.forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 720 262", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M411.996 159.402c-12.655-9.343-18.116-26.236-13.633-52.765.14-.84.502-2.324.98-4.138-2.635-1.049-5.617-1.498-8.976-1.498-29.86 0-46.485 50.303-46.485 97.062 0 24.374 9.528 36.655 23.763 36.655 19.738 0 37.879-34.26 44.351-75.316m246.149-47.158s-14.268 68.612-14.268 93.99c0 17.638 8.218 23.682 18.37 23.682 12.837 0 24.623-8.102 35.6-28.21 2.52-4.615 11.736-1.727 16.57 3.835 2.106 2.426-.318 7.317-3.638 12.913C694.144 246.502 670.643 262 646.225 262c-26.6 0-42.953-12.398-47.05-43.758C583.917 242.244 559.498 262 531.516 262c-34.02 0-48.5-18.827-48.5-52.607 0-22.281 7.505-60.23 7.505-60.23-9.966 8.981-21.522 15.475-34.824 18.172C442.624 228.594 407.632 262 360.613 262c-39.819 0-67.176-20.926-67.176-71.113 0-61.952 39.45-117.15 101.346-117.15 35.243 0 60.825 18.91 63.078 62.92 14.492-2.446 27.884-12.369 39.545-26.498 5.248-33.237 12.748-71.145 17.325-92.536.953-4.45 2.579-12.797 10.504-15.267 8.87-2.768 21.537-3.256 34.857-.608 7.384 1.467 9.261 8.367 9.261 14.533 0 16.397-7.769 52.57-21.278 85.1-9.574 36.262-16.142 72.442-16.142 101.05 0 20.673 6.84 27.485 18.309 27.485 18.72 0 40.466-26.001 50.939-61.226 1.626-16.944 5.916-44.198 8.068-54.357-11.597.557-19.01.894-19.01.894-6.034.221-11.883-2.323-11.586-7.71.394-7.096 1.37-13.925 2.565-18.734 1.196-4.809 3.938-7.793 9.768-7.896 6.255-.11 15.25-.037 24.516.185 3.122-17.875 5.746-31.825 8.266-43.421 1.007-4.641 5.35-7.313 9.82-7.815 5.35-.6 21.291-.487 28.23 1.196 6.986 1.698 9.913 7.185 8.702 12.735-2.472 11.303-7.526 39.04-7.526 39.04a6147.07 6147.07 0 0 1 46.66 2.252c7.475.335 11.327 4.544 10.131 11.125-2.016 11.055-10.246 14.608-21.875 14.875-13.262.304-27.21.731-39.765 1.185M299.32 23.772c0 77.229-41.424 237.729-105.603 237.729-48.42 0-58.144-54.148-60.644-123.13-24.267 52.943-37.112 80.43-53.148 107.952C72.35 259.32 63.246 262 53.302 262c-8.757 0-31.342-2.926-36.603-6.19-5.261-3.262-8.153-6.21-10.73-18.68-11.925-57.698-4.212-132.317 7.642-191.802 2.071-10.393 4.737-15.415 11.917-20.121 7.659-5.019 24.632-5.373 35.686-5.566 5.868-.103 9.279 4.121 7.803 11.274-10.677 51.94-23.21 123.105-13.445 196.466 0 0 36.936-73.192 75.224-164.314 7.685-18.291 9.405-24.297 16.715-25.917 9.568-2.12 16.467-2.13 26.102-.959 6.357.774 9.553 2.432 9.309 11.24-.667 23.745-2.257 50.615-2.257 78.511 0 47.204 5.064 100.27 23.246 100.27 24.419 0 45.87-113.537 38.294-211.097-.524-6.738 1.912-13.583 9.113-14.33C254.925.412 262.227 0 270.457 0c18.748 0 28.864 3.343 28.864 23.772" }) }));
const WoltLogo$1 = WoltLogo;
export {
  TypographyComponents as Typography,
  WoltLogo$1 as WoltLogo,
  cbSemanticCssVars,
  colors,
  darkModeElevationBGColors,
  elevationCss,
  experimental_cbElevatedClassName,
  experimental_cbSemanticCssVars,
  experimental_elevationCss,
  semanticColors,
  typographyCss
};
//# sourceMappingURL=index.js.map
